import React, { useState } from 'react'
import { IconInfoCircle } from '@tabler/icons-react'

import { Accordion } from '@/components/accordions/Accordion'
import { Pill } from '@/components/pills/Pill'
import { Table } from '@/components/tables/Table/Table'

import './GenericSetupGuide.scss'

const START_EVENT = `{
  "eventType": "callAnswered",
  "callId": "CA-4471",
  "agent":    { "email": "jordan@acme.com" },
  "customer": { "number": "+15105550133" },
  "queue":    { "name": "Retention" },
  "direction": "outgoing",
  "customFields": [
    { "name": "account_tier", "value": "gold" }
  ]
}`

const STOP_EVENT = `{
  "eventType": "callEnded",
  "callId": "CA-4471",
  "agent": { "email": "jordan@acme.com" },
  "disposition": "Sale"
}`

const DISPO_EVENT = `{
  "state": "dispositions",
  "operator_call_id": "CA-4471",
  "operator_target": { "email": "jordan@acme.com" },
  "call_dispositions": "Sale"
}`

// Each row is one form field, the path that maps the example payload above, and why.
const MAPPING = [
  ['Agent Path', 'agent.email', 'Who to start the call for. Must match the agent’s Balto login.'],
  ['Event Type Path', 'eventType', 'The field whose value decides what the event means.'],
  ['Values that START', 'callAnswered', 'Matched ignoring case and spaces.'],
  ['Values that STOP', 'callEnded', 'A value may not appear in more than one list.'],
  ['VOIP Call ID Path', 'callId', 'Must be identical on the start and the stop event.'],
  ['VOIP Customer ID Path', 'customer.number', 'Optional. Phone or account number.'],
  ['Campaign Name Fields', 'queue.name', 'One or more paths, joined with _ in row order.'],
  ['Direction Path', 'direction', '"outgoing" is understood already — no mapping row needed.'],
  [
    'Call Metadata',
    'customFields[name=account_tier].value',
    'Keyed values on the call record; can drive dynamic checklists.',
  ],
  ['Disposition Path', 'disposition', 'Read off the stop event.'],
]

const PATHS = [
  ['call.id', 'A nested value.'],
  ['customFields[name=account_tier].value', 'The list item whose "name" is account_tier.'],
  [
    'members[extension?].extension.number',
    'The list item that HAS an "extension" key — for payloads that label each item by key rather than by a field.',
  ],
  ['msg|json.call_id', 'Decode a field whose value is JSON-encoded text, then keep going.'],
  [
    'events.0.type',
    'By position — only if the vendor guarantees order. A bare number: events[0] resolves to nothing.',
  ],
]

const Code = ({ children }) => <pre className="generic-guide-code">{children}</pre>

/**
 * The reference an implementation engineer reads while onboarding a new vendor.
 *
 * Collapsed by default: it is long, and someone editing an already-working config
 * does not need it. It deliberately repeats the connector's README rather than
 * linking to it, because the people configuring this do not have the repo open.
 */
export const GenericSetupGuide = () => {
  const [expanded, setExpanded] = useState(false)

  return (
    <Accordion
      styled
      id="generic-setup-guide"
      title="Setup guide: mapping a vendor's payload"
      // The form is a column of h4 section headings, so a bordered box with a bold
      // title and a small chevron reads as one more heading. The icon separates it
      // from them, and the pill names the action -- same reasoning, and the same
      // pattern, as the expandable tables in kodiMarkdown.
      icon={<IconInfoCircle className="icon-svg" />}
      actions={
        <Pill brand small>
          {expanded ? 'Hide' : 'Show me how'}
        </Pill>
      }
      expanded={expanded}
      handleToggle={() => setExpanded((open) => !open)}
      className="generic-setup-guide"
    >
      <div data-testid="generic-setup-guide-body">
        <p>
          The vendor POSTs a JSON body to the webhook URL above every time something happens on a
          call. Every field below is a <strong>path</strong> into that body, so Balto knows where
          each value lives. Nothing here is vendor-specific code &mdash; if you can describe the
          payload, the integration works.
        </p>

        <h4>1. Get a real payload first</h4>
        <p>
          Point the vendor at the webhook URL and place one test call. Each payload we receive is
          logged, so you can copy a real body rather than guessing at field names. Configure from
          that, not from the vendor&apos;s documentation &mdash; the two disagree more often than
          not.
        </p>

        <h4>2. A worked example</h4>
        <p>Say the vendor sends this when an agent answers:</p>
        <Code>{START_EVENT}</Code>
        <p>and this when the call ends:</p>
        <Code>{STOP_EVENT}</Code>
        <p>That payload maps to:</p>
        <div className="table-scroll wrap mb">
          <Table compact>
            <thead>
              <tr>
                <th>Field</th>
                <th>Value to enter</th>
                <th>Why</th>
              </tr>
            </thead>
            <tbody>
              {MAPPING.map(([field, value, why]) => (
                <tr key={field}>
                  <td>{field}</td>
                  <td>
                    <code>{value}</code>
                  </td>
                  <td>{why}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>

        <h4>3. Path syntax</h4>
        <div className="table-scroll wrap mb">
          <Table compact>
            <tbody>
              {PATHS.map(([path, meaning]) => (
                <tr key={path}>
                  <td>
                    <code>{path}</code>
                  </td>
                  <td>{meaning}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <p>
          Keys are <strong>case-sensitive</strong>; the value inside <code>[brackets]</code> is not.
          Prefer selecting from a list by field or by key rather than by position &mdash; most
          vendors do not guarantee order.
        </p>

        <h4>4. Dispositions</h4>
        <p>
          Most vendors put the outcome on the event that ends the call, as in the example above. Set{' '}
          <strong>Disposition Path</strong> and you are done.
        </p>
        <p>
          Some send it on a <em>separate</em> event instead, after the call has already ended:
        </p>
        <Code>{DISPO_EVENT}</Code>
        <p>
          For that, add <code>dispositions</code> under{' '}
          <strong>Values for a DISPOSITION-only event</strong>. Such an event records the outcome
          and does <strong>not</strong> start or stop anything. Because this payload names the call
          and the agent differently (<code>operator_call_id</code>,{' '}
          <code>operator_target.email</code>), set the two override paths as well; leave them empty
          when the vendor reuses one shape for every event.
        </p>
        <p className="generic-guide-warning">
          If the vendor&apos;s disposition arrives on the event that <em>ends</em> the call, put
          that value under <strong>STOP</strong> and leave the disposition-only list empty. Listing
          it as disposition-only would mean the call never stops.
        </p>

        <h4>5. What happens when a path doesn&apos;t match</h4>
        <ul>
          <li>
            <strong>One path fails to resolve</strong> &mdash; that field is left out and the event
            still proceeds. Check the logs to see what each path produced.
          </li>
          <li>
            <strong>The event matches no list</strong> &mdash; it is ignored, and the log says which
            value it saw and what it compared it against.
          </li>
          <li>
            <strong>No call id resolves</strong> &mdash; the call still starts, but duplicate and
            out-of-order stop events can no longer be filtered, and a disposition cannot be recorded
            at all.
          </li>
          <li>
            <strong>The agent isn&apos;t a Balto user</strong> &mdash; ignored quietly. A PBX sends
            events for everyone in the tenant, not just your agents.
          </li>
        </ul>
      </div>
    </Accordion>
  )
}
